// src/utils/teamHealth.js
//
// The team checkup a Super Admin sees after logging in: people who are not
// where the Teams screen, the load pickers and the Companies tab expect them
// to be, each with a suggested fix.
//
// Only companies that use teams are checked. A company without teams has no
// such thing as "not on a team", so a tenant that does not use them never sees
// this at all.
//
// The suggestion comes from evidence the system already has, strongest first:
// who dispatches the driver now, then who ran their recent loads. It is only a
// suggestion — the Super Admin applies it, changes it, or skips it.
//
// Pure functions: no reads, no writes. The window (TeamHealthCheck) fetches,
// this decides, useTeamActions writes.

import { getCompanyTeams, getUserTeamIds, getDriverOwnTeamId } from './teams';

const rolesOf = (u) => (Array.isArray(u?.role) ? u.role.filter(Boolean) : (u?.role ? [u.role] : []));
const isActiveUser = (u) => u && u.active !== false;
const isActiveDriver = (d) => d && !d.isDeleted && d.status === 'Active';
const isDispatcherUser = (u) => {
  const r = rolesOf(u);
  return r.includes('Dispatcher') && !r.includes('Main Admin') && !r.includes('Super Admin');
};
const nameOf = (p) => p?.name || p?.email || 'Unnamed';

/** Most frequent value, with its count. Ties go to the first seen (newest load). */
function majority(values) {
  const counts = new Map();
  for (const v of values) if (v) counts.set(v, (counts.get(v) || 0) + 1);
  let best = null;
  for (const [v, n] of counts) if (!best || n > best.count) best = { value: v, count: n };
  return best;
}

/**
 * @param {object}   args
 * @param {object[]} args.companies  every company in the tenant
 * @param {object[]} args.users      every user in the tenant
 * @param {object[]} args.drivers    every driver in the tenant
 * @param {object[]} [args.loads]    recent loads, newest first: { driverId, dispatcherId }
 * @returns {object[]} issues — see the shapes below
 */
export function findTeamIssues({ companies = [], users = [], drivers = [], loads = [] }) {
  const parents = companies.filter(c => !c.parentCompanyId && c.type !== 'subdivision');
  const teamCompanyIds = new Set(parents.filter(c => getCompanyTeams(c).length > 0).map(c => c.id));
  if (teamCompanyIds.size === 0) return [];

  const teamInfo = new Map();
  for (const c of parents) for (const t of getCompanyTeams(c)) teamInfo.set(t.id, { team: t, company: c });

  const userById = new Map(users.map(u => [u.id, u]));
  const driverById = new Map(drivers.map(d => [d.id, d]));
  // Teams that exist, by the same rule every screen now uses.
  const teamsOfUser = (u) => getUserTeamIds(u).filter(t => teamInfo.has(t));
  const dispatcherTeams = (u) => (isActiveUser(u) && isDispatcherUser(u) ? teamsOfUser(u) : []);
  const teamDispatchers = (teamId) => users.filter(u => dispatcherTeams(u).includes(teamId));
  const ownTeam = (d) => getDriverOwnTeamId(d, parents);

  const loadsOfDriver = (id) => loads.filter(l => l.driverId === id);
  const loadsOfDispatcher = (id) => loads.filter(l => l.dispatcherId === id);

  // A dispatcher's team, preferring one in the given company.
  const teamFor = (u, companyId) => {
    const ts = dispatcherTeams(u);
    return ts.find(t => teamInfo.get(t).company.id === companyId) || ts[0] || null;
  };

  const teamLabel = (teamId) => {
    const i = teamInfo.get(teamId);
    return i ? `${i.team.name} (${i.company.name})` : 'a team';
  };

  const evidence = (driverId, dispatcherId) => {
    const recent = loadsOfDriver(driverId);
    if (recent.length === 0) return '';
    const n = recent.filter(l => l.dispatcherId === dispatcherId).length;
    return n > 0 ? ` — ran ${n} of their last ${recent.length} loads` : '';
  };

  /** Where a driver should be, or null when there is nothing to go on. */
  const suggestForDriver = (d, keepTeam) => {
    const recent = loadsOfDriver(d.id);
    const busiest = majority(recent.map(l => l.dispatcherId).filter(id => dispatcherTeams(userById.get(id)).length > 0));

    if (keepTeam) {
      const onTeam = teamDispatchers(keepTeam);
      const fromLoads = majority(recent.map(l => l.dispatcherId).filter(id => onTeam.some(u => u.id === id)));
      if (fromLoads) {
        const u = userById.get(fromLoads.value);
        return { teamId: keepTeam, dispatcherId: u.id, reason: `${nameOf(u)} is on their team${evidence(d.id, u.id)}` };
      }
      if (onTeam.length === 1) {
        return { teamId: keepTeam, dispatcherId: onTeam[0].id, reason: `${nameOf(onTeam[0])} is the only dispatcher on their team` };
      }
      return null;
    }

    const current = userById.get(d.dispatcherId);
    if (current && teamFor(current, d.parentCompanyId)) {
      return {
        teamId: teamFor(current, d.parentCompanyId),
        dispatcherId: current.id,
        reason: `${nameOf(current)} dispatches them${evidence(d.id, current.id)}`,
      };
    }
    if (busiest) {
      const u = userById.get(busiest.value);
      return { teamId: teamFor(u, d.parentCompanyId), dispatcherId: u.id, reason: `${nameOf(u)}${evidence(d.id, u.id)}` };
    }
    return null;
  };

  const issues = [];

  // ---- Drivers -----------------------------------------------------------
  for (const d of drivers) {
    if (!isActiveDriver(d)) continue;
    const team = ownTeam(d);
    const inTeamCompany = teamCompanyIds.has(d.parentCompanyId);
    const strayTeam = d.teamId && !team ? teamInfo.get(d.teamId) : null;

    if (!team) {
      if (!inTeamCompany && !strayTeam && d.parentCompanyId) continue; // company without teams
      const problem = !d.parentCompanyId
        ? 'No company'
        : strayTeam
          ? `On a team of ${strayTeam.company.name}, but belongs to ${d.parentCompanyName || 'another company'}`
          : `No team in ${d.parentCompanyName || 'their company'}`;
      issues.push({ key: `driver:${d.id}`, kind: 'driver', id: d.id, name: nameOf(d), problem, suggestion: suggestForDriver(d, null) });
      continue;
    }

    const disp = d.dispatcherId ? userById.get(d.dispatcherId) : null;
    let problem = null;
    if (!d.dispatcherId) problem = 'No dispatcher';
    else if (!disp || !isActiveUser(disp)) problem = `Dispatcher ${nameOf(disp) || ''} is deactivated`.replace('  ', ' ');
    else if (!dispatcherTeams(disp).includes(team)) problem = `Dispatcher ${nameOf(disp)} is not on ${teamLabel(team)}`;
    if (problem) {
      issues.push({ key: `driver:${d.id}`, kind: 'driver', id: d.id, name: nameOf(d), problem, suggestion: suggestForDriver(d, team) });
    }
  }

  // ---- Dispatchers -------------------------------------------------------
  for (const u of users) {
    if (!isActiveUser(u) || !isDispatcherUser(u)) continue;
    const companiesOfUser = Array.isArray(u.assignedParentCompanyIds) ? u.assignedParentCompanyIds : [];
    const inTeamCompany = companiesOfUser.some(id => teamCompanyIds.has(id));
    if (teamsOfUser(u).length > 0) continue;
    if (companiesOfUser.length > 0 && !inTeamCompany) continue; // company without teams

    // Where their work is: the teams of drivers linked to them, else of the
    // drivers on loads they dispatched.
    const linked = drivers.filter(d => isActiveDriver(d) && d.dispatcherId === u.id).map(ownTeam);
    const fromLoads = loadsOfDispatcher(u.id).map(l => ownTeam(driverById.get(l.driverId)));
    const best = majority(linked.filter(Boolean)) || majority(fromLoads.filter(Boolean));
    const suggestion = best
      ? {
          teamId: best.value,
          reason: linked.some(Boolean)
            ? `${best.count} of their drivers are on ${teamLabel(best.value)}`
            : `${best.count} of their recent loads are ${teamLabel(best.value)} drivers`,
        }
      : null;

    issues.push({
      key: `user:${u.id}`,
      kind: 'user',
      id: u.id,
      name: nameOf(u),
      problem: companiesOfUser.length === 0 ? 'Dispatcher with no company and no team' : 'Dispatcher on no team',
      suggestion,
    });
  }

  // ---- Leftover team entries ---------------------------------------------
  // Ignored everywhere already; cleaning them up only tidies the record.
  for (const u of users) {
    if (!isActiveUser(u)) continue;
    const raw = Array.isArray(u.teamIds) ? u.teamIds.filter(Boolean) : [];
    const counted = getUserTeamIds(u);
    const leftovers = raw.filter(t => !counted.includes(t) && teamInfo.has(t));
    if (leftovers.length === 0) continue;
    if (issues.some(i => i.key === `user:${u.id}`)) continue;
    issues.push({
      key: `leftover:${u.id}`,
      kind: 'leftover',
      id: u.id,
      name: nameOf(u),
      problem: `Old entry for ${leftovers.map(teamLabel).join(', ')} — not in that company`,
      suggestion: { cleanup: true, reason: 'Remove the old entry. Nothing else changes.' },
    });
  }

  return issues;
}

/** Driver and dispatcher ids whose recent loads would sharpen a suggestion. */
export function peopleNeedingLoads(issues) {
  return {
    driverIds: issues.filter(i => i.kind === 'driver').map(i => i.id),
    dispatcherIds: issues.filter(i => i.kind === 'user' && !i.suggestion).map(i => i.id),
  };
}

/** "Company / Team" for every team, for the change-it-yourself dropdown. */
export function allTeamsForPicker(companies = []) {
  return companies
    .filter(c => !c.parentCompanyId && c.type !== 'subdivision')
    .flatMap(c => getCompanyTeams(c).map(t => ({ id: t.id, label: `${c.name} / ${t.name}`, company: c })));
}
